import React, { useEffect, useState } from "react";

import DefaultAvatar from "@/assets/images/default_avatar.png";
import {
  isExpiringPresignedUrl,
  loadPresignedImageObjectUrl,
} from "@/utils/presignedImageCache";

// A user's avatar image, with a guaranteed fallback to the bundled placeholder.
//
// The fallback is held in React state rather than written onto the DOM node from
// inside the error handler. An `onError` prop is registered through React's
// synthetic event system, NOT as the element's own `onerror` property, so the
// familiar `imageError.currentTarget.onerror = null` guard clears a property
// React never set and therefore stops nothing. If the placeholder ITSELF fails
// to load, that handler fires again, re-assigns the same unloadable source, and
// the browser re-requests it — an unbounded error/assign/request loop that shows
// up as an avatar flickering "not found" while the network tab fills.
//
// Routing the fallback through state breaks the loop: once `failedAvatarUrl`
// matches the current URL a repeat error sets it to the same value, React bails
// out of the re-render, the `src` attribute is never rewritten, and no further
// request is made.
type UserAvatarProps = {
  // The user's avatar URL. May be empty or unset for a user who has never
  // uploaded one; may be unloadable if the stored object has since expired.
  avatarUrl: string | undefined;
  // Pass "" where an adjacent element already names the user, so screen readers
  // do not announce the same name twice.
  alt: string;
  className: string;
};

export default function UserAvatar({ avatarUrl, alt, className }: UserAvatarProps) {
  // The specific URL that failed, rather than a boolean: a paginated list keys
  // its rows by index, so React reuses the same <img> for a different user as
  // the page changes. Comparing against the current URL gives every new avatar
  // its own chance to load instead of letting one broken image suppress every
  // avatar that later occupies that row.
  const [failedAvatarUrl, setFailedAvatarUrl] = useState<string | null>(null);
  const [cachedObjectUrl, setCachedObjectUrl] = useState<string | null>(null);
  const [isCachingAvatar, setIsCachingAvatar] = useState<boolean>(false);

  // User.avatar is presigned with the same 15-minute expiry as the vulnerability
  // screenshots, so an avatar on a page left open silently decays to the
  // placeholder and stays there. Copying the bytes into a blob once detaches the
  // picture from the signature that fetched it.
  //
  // Only presigned URLs go through this. The bundled placeholder and any
  // externally hosted avatar have no deadline and are rendered directly.
  useEffect(() => {
    setCachedObjectUrl(null);

    if (!isExpiringPresignedUrl(avatarUrl)) {
      setIsCachingAvatar(false);
      return;
    }

    let hasUnmounted = false;
    setIsCachingAvatar(true);

    loadPresignedImageObjectUrl(avatarUrl as string)
      .then((objectUrl) => {
        if (!hasUnmounted) setCachedObjectUrl(objectUrl);
      })
      .catch(() => {
        // Expected until CORS GET is deployed. The direct <img> below still
        // works, so this degrades to the previous behaviour rather than failing.
      })
      .finally(() => {
        if (!hasUnmounted) setIsCachingAvatar(false);
      });

    return () => {
      hasUnmounted = true;
    };
  }, [avatarUrl]);

  // Held bytes: nothing left to expire, so no error handling is needed.
  if (cachedObjectUrl) {
    return <img src={cachedObjectUrl} alt={alt} className={className} />;
  }

  // Deliberately NOT the placeholder while the fetch is in flight: showing the
  // grey default first and swapping to the real face is the very flicker this
  // component exists to remove. A neutral shape holds the space instead, and the
  // placeholder stays reserved for a genuine failure.
  if (isCachingAvatar) {
    return <div className={`${className} bg-gray-100 animate-pulse`} aria-hidden="true" />;
  }

  // An empty src resolves against the current document URL, so the browser
  // re-requests the page itself and then fails to decode the HTML as an image.
  // Fall straight through to the placeholder instead.
  const hasUsableAvatarUrl = Boolean(avatarUrl) && failedAvatarUrl !== avatarUrl;
  const resolvedAvatarSrc = hasUsableAvatarUrl ? (avatarUrl as string) : DefaultAvatar;

  return (
    <img
      src={resolvedAvatarSrc}
      alt={alt}
      className={className}
      onError={() => setFailedAvatarUrl(avatarUrl ?? null)}
    />
  );
}
